import React, { MouseEvent, useCallback, memo } from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { subscriptionStatusInfo, UserInfo, userPlanInfo } from 'types/user';
import { formatDateWithNewRule, getShortPeriodLabel } from 'utils/utils';
import usePlanStatus from 'hooks/usePlanStatus';
import { useUserStore } from 'contexts/useUserStore';
import { EXHAUSTED_COLOR } from 'utils/constants';

const UserPlanMenu = () => {
  const { userInfo, setShowSubscriptionDialog } = useUserStore();
  const { plan } = userInfo as UserInfo;
  const { type = 'free' } = plan || {};
  const {
    isFree,
    subscriptionStatus,
    endDate,
    remainingDeepResearch,
    totalDeepResearch,
    showRemainingDeepResearch,
    deepResearchPeriod,
    remainingDocuments,
    totalDocuments,
    showRemainingDocuments,
    documentPeriod
  } = usePlanStatus();

  const userPlan = {
    ...plan,
    ...userPlanInfo[type],
    endDateInfo: subscriptionStatusInfo[subscriptionStatus]
  };

  const { name: planName = '', endDateInfo } = userPlan;

  const handleSubscription = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setShowSubscriptionDialog(true);
    },
    [setShowSubscriptionDialog]
  );

  return (
    <>
      <Box width="100%" pb="20px">
        <Stack direction="row" sx={{ width: '100%', alignItems: 'flex-start' }}>
          <Typography
            color="text.secondary"
            variant="caption"
            lineHeight="137%"
            letterSpacing={0}
          >
            Current plan
          </Typography>
          {endDate && (
            <Chip
              label={
                <Typography
                  color={endDateInfo?.color || 'text.secondary'}
                  variant="caption"
                >
                  {endDateInfo?.text} {formatDateWithNewRule(endDate)}
                </Typography>
              }
              sx={{
                height: 24,
                backgroundColor: endDateInfo.bgColor,
                ml: 'auto'
              }}
              size="small"
            />
          )}
        </Stack>
        <Typography variant="h6" fontWeight={500} lineHeight="142%" mb={0.5}>
          {planName}
        </Typography>
        {showRemainingDeepResearch && remainingDeepResearch !== null && (
          <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
            <Typography
              variant="body2"
              sx={{
                color:
                  remainingDeepResearch === 0 ? EXHAUSTED_COLOR : 'text.primary'
              }}
            >
              Deep researches{getShortPeriodLabel(deepResearchPeriod)}:{' '}
              <Box component="span" fontWeight={600}>
                {remainingDeepResearch}
                {totalDeepResearch !== null ? `/${totalDeepResearch}` : ''}
              </Box>
            </Typography>
          </Stack>
        )}
        {showRemainingDocuments && remainingDocuments !== null && (
          <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
            <Typography
              variant="body2"
              sx={{
                color:
                  remainingDocuments === 0 ? EXHAUSTED_COLOR : 'text.primary'
              }}
            >
              Other docs{getShortPeriodLabel(documentPeriod)}:{' '}
              <Box component="span" fontWeight={600}>
                {remainingDocuments}
                {totalDocuments !== null ? `/${totalDocuments}` : ''}
              </Box>
            </Typography>
          </Stack>
        )}
        {isFree && (
          <Button
            variant="outlined"
            sx={{
              marginTop: 2,
              padding: '6px 20px',
              height: 36,
              fontSize: 16,
              textTransform: 'none',
              pointerEvents: 'auto'
            }}
            onClick={handleSubscription}
            fullWidth
          >
            Upgrade
          </Button>
        )}
      </Box>
    </>
  );
};

export default memo(UserPlanMenu);
